<template>
  <div class="flex flex-col relative">
    <!-- Header Section -->
    <header v-if="enable_header" :class="['shadow-lg', getColor(100, 'bg')]">
      <nav
        class="flex items-center justify-between w-full h-20 md:h-20 lg:h-24 px-4 md:px-12 py-4 md:py-10"
      >
        <!-- Logo or Event Type Name -->
        <div
          class="flex items-center justify-center w-full md:w-auto space-x-6"
        >
          <a :href="$TWORK_INFO.url?.homepage || '#'" class="flex items-center">
            <!-- Display logo if available, otherwise display eventType name -->
            <img
              v-if="eventType.images.logo"
              :src="eventType.images.logo.big"
              alt="Logo"
              class="h-12 md:h-14 w-auto header-logo"
            />
            <span
              v-else
              :class="['text-2xl font-bold', getColor(500, 'text')]"
              >{{ eventType.name }}</span
            >
          </a>
        </div>

        <!-- Mobile Menu Button -->
        <font-awesome-icon
          id="menu-button"
          icon="bars"
          class="sidebar-button"
          :class="['h-8 w-8 cursor-pointer block', getColor(500, 'text')]"
          @click="toggleSidebar"
        />
        <div class="auth-module">
          <AuthBarModule :eventType="eventType" :getColor="getColor" />
        </div>
      </nav>

      <div
        v-if="eventTypes.length > 1"
        :class="[
          'flex text-lg font-medium event-types-tabs p-4 shadow-lg',
          getColor(50, 'bg'),
        ]"
      >
        <ul class="flex items-center space-x-4 ml-3 md:ml-10">
          <li
            v-for="(eventTypeItem, index) in eventTypes"
            :key="eventTypeItem.type"
            class="flex items-center hover:scale-105"
          >
            <!-- Event Type Link -->
            <router-link
              :to="'/' + eventTypeItem.path"
              exact-active-class="text-primary font-semibold"
              :class="[
                'transition-all',
                getColor(500, 'text'),
                getColor(400, 'hover:text'),
              ]"
            >
              {{ eventTypeItem.plural }}
            </router-link>
            <!-- Divider between items, excluding last item -->
            <span
              v-if="index < eventTypes.length - 1"
              :class="['divider ml-4', getColor(200, 'bg')]"
            ></span>
          </li>
        </ul>
      </div>
    </header>

    <div
      v-if="eventType.enable_hero"
      class="bg-gray-50 flex items-center flex-grow hero-section"
    >
      <!-- Hero Background with Overlay -->
      <div class="relative w-full py-16" :style="heroBackgroundStyle">
        <div class="absolute inset-0 bg-black bg-opacity-50 w-full"></div>

        <div class="container mx-auto text-left text-white relative z-10 px-5">
          <div class="flex flex-wrap md:flex-nowrap items-center xl:mx-10">
            <!-- Text Content with Padding for Mobile -->
            <div
              class="hero-container w-full md:w-1/2 px-4 md:px-0 justify-center"
            >
              <h1
                v-if="eventType.hero.title"
                v-html="eventType.hero.title"
                class="text-3xl md:text-5xl font-medium mb-4 md:mb-6 text-white hero-title"
              ></h1>
              <p
                v-if="eventType.hero.subtitle"
                v-html="eventType.hero.subtitle"
                class="text-base md:text-xl text-white hero-subtitle"
              ></p>

              <!-- Responsive Button Layout -->
              <div
                v-if="eventType.hero.buttons"
                class="flex flex-wrap gap-2 mt-4 md:mt-6 hero-buttons justify-start"
              >
                <a
                  v-for="(button, index) in eventType.hero.buttons"
                  :key="index"
                  :href="button.link"
                  class="flex items-center justify-center py-2 px-5 rounded-lg text-white shadow-md transition-transform transform hover:scale-105"
                  :class="[
                    getColor(500, 'bg'),
                    getColor(600, 'hover:bg'),
                    eventType.hero.buttons.length > 2 ? 'w-full sm:w-auto' : '',
                  ]"
                >
                  <span v-html="button.text"></span>
                </a>
              </div>
            </div>

            <!-- Conditional Rendering for Video or Image - Hidden on Mobile -->
            <div class="hidden md:block w-1/2 pl-16">
              <div
                v-if="eventType.hero.video"
                class="relative w-full"
                style="padding-top: 56.25%"
              >
                <iframe
                  class="absolute top-0 left-0 w-full h-full rounded-xl shadow-xl"
                  :src="eventType.hero.video"
                  title="Hero Video"
                  frameborder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowfullscreen
                ></iframe>
              </div>
              <img
                v-else-if="eventType.hero.image"
                :src="eventType.hero.image"
                class="w-full object-cover rounded-xl"
                alt="Hero Image"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      ref="categoriesWrapper"
      class="p-6 bg-gray-100 border-b border-gray-300 flex overflow-x-auto whitespace-nowrap justify-between items-center space-x-4"
    >
      <div v-show="showLeftArrow" class="flex-shrink-0">
        <button @click="scrollCategories('left')" class="focus:outline-none">
          <font-awesome-icon
            icon="chevron-left"
            class="text-gray-500 hover:text-gray-700"
          />
        </button>
      </div>

      <!-- Categories List (touch-enabled) -->
      <div
        ref="categoriesList"
        @scroll="handleScroll"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
        @mousedown="handleMouseDown"
        class="categories-list relative flex flex-nowrap space-x-4 p-1 min-w-0 overflow-x-hidden whitespace-nowrap scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200"
      >
        <CategoryDropdown
          v-for="category in topLevelCategories"
          :key="category.id"
          :category="category"
          :globalOpenCategory="globalOpenCategory"
          :getColor="getColor"
          @categorySelected="selectCategory"
          @updateGlobalOpenCategory="updateGlobalOpenCategory"
        />
      </div>

      <!-- Right Chevron (scroll right) -->
      <div v-show="showRightArrow" class="flex-shrink-0">
        <button @click="scrollCategories('right')" class="focus:outline-none">
          <font-awesome-icon
            icon="chevron-right"
            class="text-gray-500 hover:text-gray-700"
          />
        </button>
      </div>
      <!-- Search Input with Font Awesome Icon -->
      <div class="w-auto flex justify-end">
        <!-- Search Icon for Mobile (Always visible) -->
        <button
          @click="toggleSearch"
          class="md:hidden flex items-center ml-3 text-gray-500 focus:outline-none"
        >
          <font-awesome-icon
            icon="search"
            :class="[
              'h-6 w-6',
              isSearchOpen ? getColor(500, 'text') : 'text-gray-500',
            ]"
          />
        </button>

        <!-- Desktop Search Input (always visible on larger screens) -->
        <div
          v-show="!isMobile"
          class="search-input relative w-full max-w-md ml-10 min-w-64"
        >
          <span
            class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400"
          >
            <font-awesome-icon icon="search" />
          </span>
          <input
            type="text"
            v-model="searchQuery"
            @input="debouncedHandleSearch"
            :placeholder="translatedPlaceholder"
            :class="[
              'border-2 rounded-lg pl-10 pr-4 bg-white py-2 w-full focus:outline-none',
              getColor(400, 'border'),
              getColor(400, 'focus:border'),
              getColor(400, 'focus:ring'),
            ]"
          />
        </div>
      </div>
    </div>

    <MobileSidebar
      v-show="isSidebarOpen"
      :isSidebarOpen="isSidebarOpen"
      :eventType="eventType"
      :showSearchInput="true"
      :searchQuery="searchQuery"
      @update-search-query="updateSearchQuery"
      @close-sidebar="closeSidebar"
    />

    <div v-show="isSearchOpen && isMobile" class="w-full mt-4 px-5">
      <span class="absolute flex items-center p-3 text-gray-500">
        <font-awesome-icon icon="search" />
      </span>
      <input
        type="text"
        v-model="searchQuery"
        @input="debouncedHandleSearch"
        :placeholder="translatedPlaceholder"
        :class="[
          'border border-gray-300 rounded pl-10 pr-4 p-2 w-full focus:outline-none focus:ring-2 shadow-lg',
          getColor(500, 'focus:ring'),
        ]"
      />
    </div>

    <!-- Selected Category Display with Results Count -->
    <div
      v-if="selectedCategory || searchQuery"
      class="p-4 pt-2 bg-gray-100 flex justify-between items-center"
    >
      <span class="text-lg font-semibold text-gray-700">{{
        resultsMessage
      }}</span>
      <button
        @click="clearSelection"
        type="button"
        class="flex items-center text-red-500 hover:text-red-700"
      >
        <FontAwesomeIcon icon="times" class="mr-2" />
        <span>{{ $translate("Clear Selection") }}</span>
      </button>
    </div>

    <!-- Events grid -->
    <div class="flex flex-col overflow-y-auto flex-grow">
      <div :class="['grid gap-4 p-3', gridColumnsClass]">
        <!-- Show skeletons if loading; otherwise, show events or no results message -->
        <template v-if="loading">
          <SkeletonCard v-for="n in skeletonCardCount" :key="`skeleton-${n}`" />
        </template>
        <template v-else-if="events.length === 0">
          <!-- No results message wrapped in a full-width card -->
          <div class="col-span-full">
            <div
              class="flex flex-col items-center justify-center text-center bg-white shadow rounded-lg no-results-card py-60"
            >
              <FontAwesomeIcon
                icon="sad-tear"
                class="text-6xl text-gray-400 mb-4"
              />
              <p class="text-gray-700 text-lg font-semibold">
                {{
                  $translate(
                    "We are sorry, but currently there are no items available."
                  )
                }}
              </p>
            </div>
          </div>
        </template>
        <template v-else>
          <EventCard v-for="event in events" :key="event.id" :event="event" />
        </template>
      </div>

      <!-- Custom Pagination Component -->
      <div
        v-if="pagination.totalPages.value > 1"
        class="flex justify-center py-10"
      >
        <ApiPagination
          :total-pages="pagination.totalPages.value"
          :current-page="pagination.currentPage.value"
          :has-more-pages="pagination.hasMorePages.value"
          @pagechanged="handlePageChange"
        />
      </div>
      <!-- Footer Links Section -->
      <FooterModule
        v-if="!isSidebarOpen"
        :getColor="getColor"
        :isSidebarOpen="isSidebarOpen"
        :eventType="eventType"
      />
    </div>
  </div>
  <!--  <ChatWrapper /> -->
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";
import { listEvents } from "@/composables/listEvents";
import { usePagination } from "@/composables/usePagination";
import EventCard from "@/components/default/card/EventCard.vue";
import SkeletonCard from "@/components/default/card/SkeletonCard.vue";
import {
  getEventTypes,
  getEventType,
  findCategoryBySlug,
  generateResultsMessage,
  getColorClass,
} from "@/utils/helper";
import ApiPagination from "@/components/general/ApiPagination.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import {
  faFacebook,
  faTwitter,
  faLinkedin,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import { translate } from "@/utils/translate";
import CategoryDropdown from "@/components/general/CategoryDropdown.vue";
import debounce from "lodash/debounce";
import AuthBarModule from "./module/bar/AuthModule.vue";
import MobileSidebar from "../general/MobileSidebar.vue";
import FooterModule from "./module/main/FooterModule.vue";

/* Chat Import */
//import ChatWrapper from '@/components/default/module/chat/ChatWrapper.vue'; // Adjust the path as necessary

library.add(faFacebook, faTwitter, faLinkedin, faYoutube);

// State and logic setup
const route = useRoute();
const router = useRouter();
const { events, loading, fetchEvents, totalItems, currentPage, lastPage } =
  listEvents();
const eventTypes = computed(() => getEventTypes());
const tabsDisabled = computed(() => eventTypes.value.length === 1);
const eventType = computed(() =>
  getEventType(
    route.params.type ||
      (tabsDisabled.value ? eventTypes.value[0].type : "default")
  )
);

const enable_header = ref(
  window.vueAppConfig?.settings?.enable_header || false
);
const skeletonCardCount = ref(window.vueAppConfig.settings.list_limit || 6);
const perPage = ref(window.vueAppConfig.settings.list_limit || 6);
const total = ref(0);
const selectedCategory = ref(null);
const globalOpenCategory = ref(null);
const topLevelCategories = computed(() => {
  const enable_types_category =
    window.vueAppConfig?.settings?.enable_type_category || false;
  if (enable_types_category && eventType.value.categories.length > 0) {
    return eventType.value.categories;
  }
  return window.vueAppConfig?.settings?.categories || [];
});
const searchQuery = ref("");
const categoriesList = ref(null); // Ref for categories list container
const showLeftArrow = ref(false);
const showRightArrow = ref(true);
// Variables for touch and drag behavior
let isDragging = false;
let startX = 0;
let scrollLeft = 0;
const isSearchOpen = ref(false);
const isMobile = ref(window.innerWidth < 768);

const pagination = usePagination(total, perPage);
//console.log(eventType.value)
// Sidebar state for mobile toggle
const isSidebarOpen = ref(false);

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value;
};

const closeSidebar = () => {
  isSidebarOpen.value = false;
};

// Flags to manage fetch state
let initialized = false;
let pendingCategoryId = null;

// Fetch events based on the selected page, category, and search term
const fetchEventsByPage = async (
  page,
  typeId,
  categoryId = false,
  searchTerm = ""
) => {
  if (loading.value) return;
  loading.value = true;
  events.value = [];

  const effectiveCategoryId = selectedCategory.value
    ? selectedCategory.value.id
    : categoryId;

  const fetchResult = await fetchEvents(
    typeId,
    page,
    perPage.value,
    effectiveCategoryId,
    searchTerm,
    true
  );
  if (fetchResult) {
    total.value = totalItems.value;
    currentPage.value = page;
    lastPage.value = pagination.totalPages.value;
  }

  loading.value = false;
};

// Debounced version of handleSearch
const handleSearch = () => {
  fetchEventsByPage(
    1,
    eventType.value.id,
    selectedCategory.value ? selectedCategory.value.id : false,
    searchQuery.value
  );
};
const toggleSearch = () => {
  isSearchOpen.value = !isSearchOpen.value;
};
// Define the getColorClass function
const getColor = (shade = 500, prefix = "text") => {
  return getColorClass(eventType.value, shade, prefix);
};

// Applying debounce to handleSearch
const debouncedHandleSearch = debounce(handleSearch, 300); // 300ms debounce delay

const handlePageChange = (newPage) => {
  pagination.currentPage.value = newPage;
  fetchEventsByPage(
    newPage,
    eventType.value.id,
    selectedCategory.value ? selectedCategory.value.id : false,
    searchQuery.value
  );
};
const updateSearchQuery = (newQuery) => {
  searchQuery.value = newQuery;
  handleSearch();
};
const scrollCategories = (direction) => {
  const scrollAmount = 200;
  if (direction === "left") {
    categoriesList.value.scrollLeft -= scrollAmount;
  } else if (direction === "right") {
    categoriesList.value.scrollLeft += scrollAmount;
  }
};
const handleResize = () => {
  handleScroll();
  isMobile.value = window.innerWidth < 768;
};

const handleScroll = () => {
  const list = categoriesList.value;
  const maxScrollLeft = list.scrollWidth - list.clientWidth;

  // Small threshold added to account for potential rounding issues
  const threshold = 5;

  showLeftArrow.value = list.scrollLeft > threshold;
  showRightArrow.value = list.scrollLeft < maxScrollLeft - threshold;
};

const handleTouchStart = (e) => {
  isDragging = true;
  startX = e.touches[0].pageX - categoriesList.value.offsetLeft;
  scrollLeft = categoriesList.value.scrollLeft;
};
const handleTouchMove = (e) => {
  if (!isDragging) return;
  e.preventDefault(); // Prevent default touch behavior (like page scrolling)
  const x = e.touches[0].pageX - categoriesList.value.offsetLeft;
  const walk = (x - startX) * 2; // Adjust scroll speed by multiplying the movement
  categoriesList.value.scrollLeft = scrollLeft - walk;
};
const handleTouchEnd = () => {
  isDragging = false;
};
const handleMouseDown = (e) => {
  isDragging = true;
  startX = e.pageX - categoriesList.value.offsetLeft;
  scrollLeft = categoriesList.value.scrollLeft;

  // Add event listeners for mouse movement and release
  document.addEventListener("mousemove", handleMouseMove);
  document.addEventListener("mouseup", handleMouseUp);
};
const handleMouseMove = (e) => {
  if (!isDragging) return;
  const x = e.pageX - categoriesList.value.offsetLeft;
  const walk = (x - startX) * 2; // Adjust scroll speed
  categoriesList.value.scrollLeft = scrollLeft - walk;
};
const handleMouseUp = () => {
  isDragging = false;
  document.removeEventListener("mousemove", handleMouseMove);
  document.removeEventListener("mouseup", handleMouseUp);
};
// Handle category selection
const selectCategory = (category) => {
  console.log("EventType:", eventType.value);
  if (selectedCategory.value !== category) {
    selectedCategory.value = category;
    fetchEventsByPage(1, eventType.value.id, category.id, searchQuery.value);

    if (eventType.value.id) {
      router.push({
        name: "EventListCategory",
        params: {
          type: eventType.value.path, // Ensure this is defined
          categorySlug: category.slug,
        },
      });
    } else {
      router.push({
        name: "EventListDefaultCategory",
        params: {
          categorySlug: category.slug,
        },
      });
    }
  }
};
// Update global open category state
const updateGlobalOpenCategory = (categoryId) => {
  globalOpenCategory.value = categoryId;
};

// Clear category selection and search input
const clearSelection = () => {
  selectedCategory.value = null;
  searchQuery.value = "";
  fetchEventsByPage(1, eventType.value.id);
  if (eventType.value.id) {
    router.push({
      name: "EventListType",
      params: { type: eventType.value.path },
    });
  } else {
    router.push({ name: "EventList" });
  }
};

// Computed property for results message
const resultsMessage = computed(() => {
  return generateResultsMessage(
    totalItems.value,
    selectedCategory.value,
    searchQuery.value
  );
});

const translatedPlaceholder = computed(() => {
  return translate("Search events...");
});

// Initial fetch of events when the component is mounted
onMounted(async () => {
  handleScroll();
  window.addEventListener("resize", handleResize); // Add resize listener
  const categorySlug = route.params.categorySlug;
  if (categorySlug) {
    const category = findCategoryBySlug(categorySlug);
    if (category) {
      selectedCategory.value = category;
      pendingCategoryId = category.id;
    } else {
      clearSelection();
    }
  }

  initialized = true;

  if (pendingCategoryId) {
    await fetchEventsByPage(1, eventType.value.id, pendingCategoryId);
  } else {
    await fetchEventsByPage(1, eventType.value.id);
  }
});
onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
});
// Watch route changes to update event list based on type_id
watch(
  () => eventType.value.id ?? false,
  async (typeId) => {
    if (initialized) {
      await fetchEventsByPage(
        1,
        typeId,
        selectedCategory.value ? selectedCategory.value.id : false,
        searchQuery.value
      );
    }
  },
  { immediate: false }
);
watch(
  () => topLevelCategories.value,
  () => {
    handleScroll();
  }
);
watch(
  () => route.params.categorySlug,
  async (categorySlug) => {
    if (!initialized) return;
    const category = findCategoryBySlug(categorySlug);
    if (category) {
      selectedCategory.value = category;
      await fetchEventsByPage(
        1,
        eventType.value.id,
        category.id,
        searchQuery.value
      );
    } else {
      clearSelection();
    }
  }
);

const heroBackgroundStyle = computed(() => {
  const { enable_hero, images } = eventType.value;
  if (enable_hero && images?.cover) {
    return {
      backgroundImage: `url(${images.cover.big})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    };
  } else if (enable_hero) {
    return {
      height: "auto",
      minHeight: "200px",
    };
  }
  return "";
});

const gridColumnsClass = computed(() => {
  switch (window.vueAppConfig?.settings.list_rows) {
    case 2:
      return "grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2";
    case 3:
      return "grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3";
    case 4:
      return "grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4";
    case 5:
      return "grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5";
    default:
      return "grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3";
  }
});
</script>

<style scoped>
.categories-list {
  cursor: grab;
}

.search-input input:focus {
  box-shadow: 0 0 0 1px rgba(156, 163, 175);
}

.router-link-active {
  font-weight: 700;
}

.divider {
  width: 1px;
  height: 15px;
}

/* Add styles for the sidebar if necessary */
@media (min-width: 975px) {
  .event-types-lists {
    display: flex;
    align-items: center;
    width: auto;
  }

  .sidebar-button {
    display: none;
  }
}

@media (max-width: 974px) {
  .auth-module {
    display: none;
  }
}

@media (max-width: 767px) {
  .hero-container h1,
  .hero-container p {
    text-align: center;
  }

  .hero-buttons {
    gap: 1rem;
    justify-content: center;
    align-items: center;
    width: 100%;
  }

  .hero-button {
    display: flex;
    justify-content: center;
  }

  .search-input {
    display: unset;
  }
}

@media (max-width: 600px) {
  .category-dropdown {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}
</style>
